import { Button, TextButton } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { ModalHeader, ModalFooter } from '@creditornot/cbt-modal';
import { DateTime } from 'luxon';
import React, { useContext, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Category, Item, Option, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WoltModal } from 'common/components';
import { WideAreaCheckbox } from 'common/components';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography as T } from 'common/typography';
import { download, isDefined, localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import useBulkSelection from 'modules/menu/bulk-actions/useBulkSelection';
import { MENU_MOBILE_BREAKPOINT } from 'modules/menu/constants';
import { useExportItemsTracking } from 'modules/menu/hooks/telemetryHooks';
import { ExportItemsData, ExportedItem, ExportedOption } from 'modules/menu/types';
import useMenuData from 'modules/menu/useMenuData';

import { reportCategoryItemsExport } from '../../behavioral';

type Props = {
  category: Category;
  isOpen: boolean;
  onClose: () => void;
};

const Root = styled(WoltModal)`
  width: 640px;
  max-height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
`;

const SelectAllContainer = styled(T.Body3)<{ elevated: boolean }>`
  display: flex;
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  transition: box-shadow 200ms;
  z-index: 1;
  ${({ elevated }) => elevationCss(elevated ? 2 : 0)};
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const ItemPrice = styled.span`
  margin-inline-start: 4px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;

  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding-inline-end: 88px;
  }
`;

const ItemRowRoot = styled(T.Body3)<{ selected: boolean }>`
  display: flex;
  align-items: center;
  min-height: 52px;
  background-color: ${({ selected }) => (selected ? colors.wolt4 : cbSemanticCssVars.surfaceMain)};
  transition: background-color 100ms;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;

  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }

  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const CancelButton = styled(Button).attrs({ variant: 'secondary', size: 'small' })`
  margin-inline-end: 12px;
`;

const serializeOptions = (options: Option[]): ExportedOption[] => {
  return options.map(option => {
    const { menu_id, ...data } = option;
    return data;
  });
};

const serializeItems = (items: Item[]): ExportedItem[] => {
  return items.map(item => {
    const { menu_id, ...data } = item;
    return data;
  });
};

const serializeData = (options: Option[], items: Item[]): ExportItemsData => ({
  options: serializeOptions(options),
  items: serializeItems(items),
});

const ExportItemsModal = ({ category, isOpen, onClose }: Props) => {
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const { selectedIds, selectMultiple, deselect, clearSelection } = useBulkSelection();

  const listRef = useRef<HTMLDivElement>(null);
  const [listScrolled, setListScrolled] = useState(false);
  const trackExportItems = useExportItemsTracking(category.id);

  const handleListScroll = (event: React.UIEvent) => {
    const currentListScrolled = event.currentTarget.scrollTop > 0;

    if (currentListScrolled !== listScrolled) {
      setListScrolled(currentListScrolled);
    }
  };

  const { items: allItems, options } = useMenuData();
  const items = category.item_bindings
    .map(ib => allItems.find(item => item.id === ib.item_id))
    .filter((item): item is Item => !!item);

  const handleExportItems = () => {
    try {
      const selectedItems = [...selectedIds].map(id => items.find(item => item.id === id)!);
      const optionBindings = selectedItems.reduce<OptionBinding[]>(
        (result, item) => [...result, ...item.option_bindings],
        [],
      );
      const options = getOptionsForOptionBindings(optionBindings);
      const data = JSON.stringify(serializeData(options, selectedItems));

      const filename = `item-export-${DateTime.local().toISODate()}.json`;
      download([data], 'application/json', filename);

      reportCategoryItemsExport();
      trackExportItems(
        'Success',
        selectedItems.map(item => item.id),
      );
    } catch (error) {
      trackExportItems(`Failure: ${error}`, []);
    }
    onClose();
  };

  const getOptionsForOptionBindings = (optionBindings: OptionBinding[]) =>
    optionBindings.map(ob => options.find(option => option.id === ob.option_id)).filter(isDefined);

  const allSelected = selectedIds.size === items.length;

  const handleSelectAll = () => {
    if (!allSelected) {
      selectMultiple(items.map(item => item.id));
    } else {
      clearSelection();
    }
  };

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="menu.exportItemsHeader" />
      </ModalHeader>
      <SelectAllContainer elevated={listScrolled}>
        <WideAreaCheckbox
          onToggle={handleSelectAll}
          selectionStatus={allSelected ? 'selected' : selectedIds.size > 0 ? 'partially-selected' : 'not-selected'}
        />
        <TextButton onClick={handleSelectAll} variant="brand">
          {allSelected ? (
            <FormattedMessage id="menu.bulkActions.deselectItems" />
          ) : (
            <FormattedMessage id="menu.bulkActions.selectAll" />
          )}
        </TextButton>
      </SelectAllContainer>
      <ItemList ref={listRef} onScroll={handleListScroll}>
        {items.map(item => {
          const selected = selectedIds.has(item.id);

          return (
            <ItemRowRoot
              key={item.id}
              onClick={() => {
                if (!!selected) {
                  deselect(item.id);
                } else {
                  selectMultiple([item.id]);
                }
              }}
              selected={selected}
            >
              <WideAreaCheckbox
                onToggle={() => {
                  if (!!selected) {
                    deselect(item.id);
                  } else {
                    selectMultiple([item.id]);
                  }
                }}
                selectionStatus={selected ? 'selected' : 'not-selected'}
              />
              <T.Body3 fontWeight="bold" dir="auto" lang={primaryLanguage}>
                {localize(item.name, primaryLanguage)}
              </T.Body3>
              <ItemPrice>
                (
                <MonetaryAmount amount={parseFloat(item.baseprice.amount)} currency={item.baseprice.currency} />)
              </ItemPrice>
            </ItemRowRoot>
          );
        })}
      </ItemList>
      <StyledModalFooter>
        <CancelButton onClick={onClose}>
          <FormattedMessage id="state.cancel" />
        </CancelButton>
        <Button disabled={selectedIds.size === 0} onClick={handleExportItems} variant="primary" size="small">
          <FormattedMessage id="menu.export" />
        </Button>
      </StyledModalFooter>
    </Root>
  );
};

export default React.memo(ExportItemsModal);
